import React, { useState, useEffect, useCallback } from "react";
import { convertCustomerPropertyName } from "../../utils/format";

const MemberPropertiesTab = ({ mallId, shopNo, showToastMessage, API_URL }) => {
  // 회원가입항목 관련 상태
  const [groupNames, setGroupNames] = useState([]);
  const [properties, setProperties] = useState([]);
  const [readOnlySettings, setReadOnlySettings] = useState({});
  const [unmodifiableSettings, setUnmodifiableSettings] = useState({});
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false); // 초기 로딩 완료 여부
  // 월별 구매개수제한 관련 상태
  const [maxOrderCount, setMaxOrderCount] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maxOrderInitialLoaded, setMaxOrderInitialLoaded] = useState(false);

  // 연락처 관련 상태
  const [contact, setContact] = useState({});
  const [contactSaving, setContactSaving] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactInitialLoaded, setContactInitialLoaded] = useState(false);

  // 회원등급 목록 조회
  const fetchGroupNames = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/customer-groups/?mall_id=${mallId}&shop_no=${shopNo}`,
      );
      if (!response.ok)
        throw new Error("회원등급 데이터를 불러오는데 실패했습니다.");
      const data = await response.json();
      console.log("data:", data);
      setGroupNames(data.groups || []);
    } catch (err) {
      console.error("회원등급 불러오기 실패:", err);
      showToastMessage(`회원등급 불러오기 실패: ${err.message}`, "error");
    }
  }, [API_URL, mallId, shopNo, showToastMessage]);

  // 회원가입항목 목록 조회
  const fetchProperties = useCallback(async () => {
    // 처음 로딩할 때만 로딩 상태 표시
    if (!initialLoaded) {
      setPropertiesLoading(true);
    }
    try {
      const response = await fetch(
        `${API_URL}/customer-properties/?mall_id=${mallId}&shop_no=${shopNo}`,
      );
      if (!response.ok)
        throw new Error("회원가입항목 데이터를 불러오는데 실패했습니다.");
      const data = await response.json();
      const filterPropertyKeys = [
        "member_id",
        "password",
        "cellphone",
        "company_member",
        "address2",
      ];
      const filteredProperties = data.properties.filter(
        (property) => !filterPropertyKeys.includes(property.key),
      );
      setProperties(filteredProperties || []);
      setUnmodifiableSettings(data.unmodifiable || {});
      setInitialLoaded(true); // 초기 로딩 완료 표시
    } catch (err) {
      console.error("회원가입항목 불러오기 실패:", err);
      showToastMessage(`회원가입항목 불러오기 실패: ${err.message}`, "error");
    } finally {
      if (!initialLoaded) {
        setPropertiesLoading(false);
      }
    }
  }, [API_URL, mallId, shopNo, showToastMessage, initialLoaded]);

  // 월별 구매개수제한 데이터 불러오기
  const fetchMaxOrderCount = useCallback(async () => {
    if (!maxOrderInitialLoaded) {
      setLoading(true);
    }
    try {
      const response = await fetch(
        `${API_URL}/setting/max-order-count/?mall_id=${mallId}&shop_no=${shopNo}`,
      );
      if (!response.ok) {
        throw new Error("월별 구매개수제한 데이터를 불러오는데 실패했습니다.");
      }
      const data = await response.json();
      const maxOrderData = data.max_order_count || {};

      // 초기 로딩이고 데이터가 없는 경우에만 기본값 설정
      if (
        !maxOrderInitialLoaded &&
        Object.keys(maxOrderData).length === 0 &&
        groupNames.length > 0
      ) {
        const defaultMaxOrder = {};
        groupNames.forEach((groupName) => {
          defaultMaxOrder[groupName] = "4";
        });
        setMaxOrderCount(defaultMaxOrder);
      } else {
        setMaxOrderCount(maxOrderData);
      }
      setMaxOrderInitialLoaded(true);
    } catch (err) {
      console.error("월별 구매개수제한 불러오기 실패:", err);
      showToastMessage(
        `월별 구매개수제한 불러오기 실패: ${err.message}`,
        "error",
      );
    } finally {
      if (!maxOrderInitialLoaded) {
        setLoading(false);
      }
    }
  }, [
    API_URL,
    mallId,
    shopNo,
    showToastMessage,
    maxOrderInitialLoaded,
    groupNames,
  ]);

  // 월별 구매개수제한 저장하기
  const saveMaxOrderCount = async () => {
    // 유효성 검사: 빈 값이 있는지 확인
    const emptyGroups = [];
    Object.entries(maxOrderCount).forEach(([groupName, value]) => {
      if (value === "" || value === null || value === undefined) {
        emptyGroups.push(groupName);
      }
    });

    if (emptyGroups.length > 0) {
      showToastMessage(
        `올바른 주문수 제한을 입력해주세요. (${emptyGroups.join(", ")})`,
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      // 서버 전송 시 Number로 변환
      const numberMaxOrderCount = {};
      Object.entries(maxOrderCount).forEach(([groupName, value]) => {
        numberMaxOrderCount[groupName] = Number(value);
      });

      const response = await fetch(`${API_URL}/setting/max-order-count/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mall_id: mallId,
          shop_no: shopNo,
          max_order_count: numberMaxOrderCount,
        }),
      });

      if (!response.ok) {
        throw new Error("월별 구매개수제한 저장에 실패했습니다.");
      }

      showToastMessage("월별 구매개수제한이 저장되었습니다.", "success");
    } catch (err) {
      console.error("월별 구매개수제한 저장 실패:", err);
      showToastMessage(`월별 구매개수제한 저장 실패: ${err.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  // 개수 변경 핸들러
  const handleCountChange = (groupName, value) => {
    // string으로 관리하여 0과 빈 문자열 모두 허용
    setMaxOrderCount((prev) => ({
      ...prev,
      [groupName]: value,
    }));
  };

  // 연락처 데이터 불러오기
  const fetchContact = useCallback(async () => {
    console.log("fetchContact");
    if (!contactInitialLoaded) {
      setContactLoading(true);
    }
    try {
      const response = await fetch(
        `${API_URL}/setting/contact/?mall_id=${mallId}&shop_no=${shopNo}`,
      );
      if (!response.ok) {
        throw new Error("연락처 데이터를 불러오는데 실패했습니다.");
      }
      const data = await response.json();
      const contactData = data.contact || {};

      // 초기 로딩이고 데이터가 없는 경우에만 기본값 설정
      if (
        !contactInitialLoaded &&
        Object.keys(contactData).length === 0 &&
        groupNames.length > 0
      ) {
        const defaultContact = {};
        groupNames.forEach((groupName) => {
          defaultContact[groupName] = "1544-5200";
        });
        setContact(defaultContact);
      } else {
        setContact(contactData);
      }
      setContactInitialLoaded(true);
    } catch (err) {
      console.error("연락처 불러오기 실패:", err);
      showToastMessage(`연락처 불러오기 실패: ${err.message}`, "error");
    } finally {
      if (!contactInitialLoaded) {
        setContactLoading(false);
      }
    }
  }, [
    API_URL,
    mallId,
    shopNo,
    showToastMessage,
    contactInitialLoaded,
    groupNames,
  ]);

  // 연락처 저장하기
  const saveContact = async () => {
    // 유효성 검사: 빈 값이 있는지 확인
    const emptyGroups = [];
    Object.entries(contact).forEach(([groupName, value]) => {
      if (value === "" || value === null || value === undefined) {
        emptyGroups.push(groupName);
      }
    });

    if (emptyGroups.length > 0) {
      showToastMessage(
        `올바른 연락처를 입력해주세요. (${emptyGroups.join(", ")})`,
        "error",
      );
      return;
    }

    setContactSaving(true);
    try {
      const response = await fetch(`${API_URL}/setting/contact/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mall_id: mallId,
          shop_no: shopNo,
          contact: contact,
        }),
      });

      if (!response.ok) {
        throw new Error("연락처 저장에 실패했습니다.");
      }

      showToastMessage("연락처가 저장되었습니다.", "success");
    } catch (err) {
      console.error("연락처 저장 실패:", err);
      showToastMessage(`연락처 저장 실패: ${err.message}`, "error");
    } finally {
      setContactSaving(false);
    }
  };

  // 연락처 변경 핸들러
  const handleContactChange = (groupName, value) => {
    setContact((prev) => ({
      ...prev,
      [groupName]: value,
    }));
  };

  // 수정불가 설정 변경
  const handleReadOnlyChange = async (groupName, propertyKey, isChecked) => {
    // 즉시 UI 업데이트 (낙관적 업데이트)
    setReadOnlySettings((prev) => ({
      ...prev,
      [`${groupName}_${propertyKey}`]: isChecked,
    }));

    try {
      const response = await fetch(`${API_URL}/setting/unmodifiable/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mall_id: mallId,
          shop_no: shopNo,
          group_name: groupName,
          property_key: propertyKey,
        }),
      });

      if (!response.ok) {
        throw new Error("설정 변경에 실패했습니다.");
      }

      const data = await response.json();

      if (data.updated) {
        // 서버에서 받은 최신 unmodifiable 상태를 로컬 state에 반영
        setUnmodifiableSettings((prev) => ({
          ...prev,
          [groupName]: data.unmodifiable || [],
        }));

        // 해당 그룹의 모든 설정을 서버 응답에 맞게 업데이트
        setReadOnlySettings((prev) => {
          const updated = { ...prev };
          properties.forEach((property) => {
            const key = `${groupName}_${property.key}`;
            updated[key] = data.unmodifiable.includes(property.key);
          });
          return updated;
        });

        // 성공 토스트 표시
        showToastMessage("설정 변경이 저장되었습니다.", "success");
      }
    } catch (err) {
      // API 호출 실패 시 UI를 원래 상태로 되돌림
      setReadOnlySettings((prev) => ({
        ...prev,
        [`${groupName}_${propertyKey}`]: !isChecked,
      }));

      // 에러 토스트 표시
      showToastMessage(
        `설정 변경 중 오류가 발생했습니다: ${err.message}`,
        "error",
      );
    }
  };

  // 컴포넌트 마운트 시 기본 데이터 불러오기
  useEffect(() => {
    if (mallId && shopNo) {
      fetchGroupNames();
      fetchProperties();
    }
  }, [mallId, shopNo, fetchGroupNames, fetchProperties]);

  // groupNames가 로드된 후 추가 데이터 불러오기
  useEffect(() => {
    if (mallId && shopNo && groupNames.length > 0) {
      fetchMaxOrderCount();
      fetchContact();
    }
  }, [mallId, shopNo, groupNames, fetchMaxOrderCount, fetchContact]);

  // unmodifiable 설정 초기화
  useEffect(() => {
    if (
      groupNames.length > 0 &&
      properties.length > 0 &&
      Object.keys(unmodifiableSettings).length > 0
    ) {
      const initialReadOnlySettings = {};

      groupNames.forEach((groupName) => {
        properties.forEach((property) => {
          const key = `${groupName}_${property.key}`;
          // 해당 그룹의 unmodifiable 설정에 이 property가 포함되어 있는지 확인
          initialReadOnlySettings[key] =
            unmodifiableSettings[groupName]?.includes(property.key) || false;
        });
      });

      setReadOnlySettings(initialReadOnlySettings);
    }
  }, [groupNames, properties, unmodifiableSettings]);

  const tableHeaderStyle = {
    padding: "16px 12px",
    textAlign: "left",
    fontWeight: "600",
    color: "#374151",
    borderBottom: "2px solid #e2e8f0",
  };

  const tableCellStyle = {
    padding: "16px 12px",
    borderBottom: "1px solid #e2e8f0",
    color: "#4b5563",
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: "12px",
        padding: "30px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <h2
        style={{
          margin: "0 0 16px 0",
          fontSize: "24px",
          color: "#333",
          fontWeight: "600",
          fontFamily:
            "'LINGTEASUBUN-BOLD', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        회원가입항목 수정불가
      </h2>

      {(!initialLoaded && propertiesLoading) || properties.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #4A90E2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <p style={{ color: "#666" }}>데이터를 불러오는 중...</p>
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            isolation: "isolate",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: "14px",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#f8fafc",
                }}
              >
                <th
                  style={{
                    ...tableHeaderStyle,
                    width: "180px",
                    minWidth: "180px",
                    maxWidth: "180px",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f8fafc",
                    zIndex: 10,
                  }}
                >
                  항목명
                </th>
                {groupNames.map((groupName) => (
                  <th
                    key={groupName}
                    style={{
                      ...tableHeaderStyle,
                      width: "120px",
                      minWidth: "120px",
                      maxWidth: "120px",
                      textAlign: "center",
                    }}
                  >
                    {groupName}
                  </th>
                ))}
                <th
                  style={{
                    ...tableHeaderStyle,
                    width: "auto",
                    borderBottom: "2px solid #e2e8f0",
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {properties.map((property, index) => (
                <tr
                  key={property.key}
                  style={{
                    borderBottom: "1px solid #e2e8f0",
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                  }}
                >
                  <td
                    style={{
                      ...tableCellStyle,
                      fontWeight: "500",
                      width: "180px",
                      minWidth: "180px",
                      maxWidth: "180px",
                      position: "sticky",
                      left: 0,
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                      zIndex: 20,
                      borderRight: "1px solid #e2e8f0",
                    }}
                  >
                    <div>{convertCustomerPropertyName(property.name)}</div>
                  </td>
                  {groupNames.map((groupName) => (
                    <td
                      key={groupName}
                      style={{
                        ...tableCellStyle,
                        textAlign: "center",
                        width: "120px",
                        minWidth: "120px",
                        maxWidth: "120px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            readOnlySettings[`${groupName}_${property.key}`] ||
                            false
                          }
                          onChange={(e) =>
                            handleReadOnlyChange(
                              groupName,
                              property.key,
                              e.target.checked,
                            )
                          }
                          style={{
                            width: "18px",
                            height: "18px",
                            accentColor: "#4A90E2",
                            cursor: "pointer",
                          }}
                        />
                      </label>
                    </td>
                  ))}
                  <td
                    style={{
                      ...tableCellStyle,
                      width: "auto",
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                    }}
                  ></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          marginTop: "30px",
          padding: "20px",
          backgroundColor: "#f0f9ff",
          borderRadius: "8px",
          border: "1px solid #bfdbfe",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#1e40af",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          💡 체크박스 선택 시 해당 등급 회원의 항목 수정이 제한되며, 설정은
          실시간으로 저장됩니다.
        </p>
      </div>

      {/* 구분선 */}
      <div
        style={{
          marginTop: "40px",
          marginBottom: "20px",
          height: "1px",
          backgroundColor: "#e2e8f0",
        }}
      ></div>

      {/* 월별 구매 주문수 제한 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginTop: 20,
        }}
      >
        <h2
          style={{
            fontSize: "24px",
            color: "#333",
            fontWeight: "600",
            fontFamily:
              "'LINGTEASUBUN-BOLD', -apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          월별 구매 주문수 제한
        </h2>

        {/* 저장 버튼 */}
        <button
          onClick={saveMaxOrderCount}
          disabled={saving}
          style={{
            padding: "8px 16px",
            backgroundColor: saving ? "#9ca3af" : "#4A90E2",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: saving ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onMouseEnter={(e) => {
            if (!saving) {
              e.target.style.backgroundColor = "#357ABD";
            }
          }}
          onMouseLeave={(e) => {
            if (!saving) {
              e.target.style.backgroundColor = "#4A90E2";
            }
          }}
        >
          {saving ? (
            <>
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  border: "2px solid transparent",
                  borderTop: "2px solid white",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              저장 중...
            </>
          ) : (
            <>저장</>
          )}
        </button>
      </div>

      {!maxOrderInitialLoaded && loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #4A90E2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <p style={{ color: "#666" }}>데이터를 불러오는 중...</p>
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            paddingBottom: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "16px",
              minWidth: "max-content",
            }}
          >
            {groupNames.map((groupName) => (
              <div
                key={groupName}
                style={{
                  width: "150px",
                  flexShrink: 0,
                  padding: "16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "8px",
                    textAlign: "start",
                  }}
                >
                  {groupName}
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxOrderCount[groupName]}
                  defaultValue="4"
                  onChange={(e) => handleCountChange(groupName, e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: "white",
                    transition: "border-color 0.2s",
                    boxSizing: "border-box",
                    textAlign: "center",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4A90E2";
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: "16px",
          padding: "16px",
          backgroundColor: "#f0f9ff",
          borderRadius: "6px",
          border: "1px solid #bfdbfe",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#1e40af",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          💡 각 등급별로 월별 구매 가능한 최대 주문 수를 설정할 수 있습니다. 0
          입력 시 무제한으로 설정됩니다.
        </p>
      </div>

      {/* 구분선 */}
      <div
        style={{
          marginTop: "40px",
          marginBottom: "20px",
          height: "1px",
          backgroundColor: "#e2e8f0",
        }}
      ></div>

      {/* 연락처 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginTop: 20,
        }}
      >
        <h2
          style={{
            fontSize: "24px",
            color: "#333",
            fontWeight: "600",
            fontFamily:
              "'LINGTEASUBUN-BOLD', -apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          연락처
        </h2>

        {/* 저장 버튼 */}
        <button
          onClick={saveContact}
          disabled={contactSaving}
          style={{
            padding: "8px 16px",
            backgroundColor: contactSaving ? "#9ca3af" : "#4A90E2",
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: contactSaving ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onMouseEnter={(e) => {
            if (!contactSaving) {
              e.target.style.backgroundColor = "#357ABD";
            }
          }}
          onMouseLeave={(e) => {
            if (!contactSaving) {
              e.target.style.backgroundColor = "#4A90E2";
            }
          }}
        >
          {contactSaving ? (
            <>
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  border: "2px solid transparent",
                  borderTop: "2px solid white",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              저장 중...
            </>
          ) : (
            <>저장</>
          )}
        </button>
      </div>

      {!contactInitialLoaded && contactLoading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #4A90E2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          ></div>
          <p style={{ color: "#666" }}>데이터를 불러오는 중...</p>
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            paddingBottom: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "16px",
              minWidth: "max-content",
            }}
          >
            {groupNames.map((groupName) => (
              <div
                key={groupName}
                style={{
                  width: "150px",
                  flexShrink: 0,
                  padding: "16px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "white",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "8px",
                    textAlign: "start",
                  }}
                >
                  {groupName}
                </label>
                <input
                  type="tel"
                  value={contact[groupName]}
                  defaultValue="1544-5200"
                  onChange={(e) =>
                    handleContactChange(groupName, e.target.value)
                  }
                  //placeholder="010-1234-5678"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    backgroundColor: "white",
                    transition: "border-color 0.2s",
                    boxSizing: "border-box",
                    textAlign: "center",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4A90E2";
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: "16px",
          padding: "16px",
          backgroundColor: "#f0f9ff",
          borderRadius: "6px",
          border: "1px solid #bfdbfe",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#1e40af",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          💡 각 등급별로 표시될 연락처를 설정할 수 있습니다.
        </p>
      </div>
    </div>
  );
};

export default MemberPropertiesTab;
